import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ViralizeQualificationContext,
  type RequestViralizeCourseOptions,
} from '@/contexts/viralizeQualificationContext';
import {
  useGetCourseBySlugLazyQuery,
  useMyViralizeQualificationQuery,
  useSaveMyViralizeQualificationAnswerMutation,
  useSaveMyViralizeQualificationContactMutation,
} from '@/generated/graphql';
import { hasCourseAccess } from '@/lib/courses/hasCourseAccess';
import {
  buildViralizeQualificationItinerary,
  type ViralizeQualificationSnapshot,
} from '@/lib/viralize-qualification/buildItinerary';
import type { ViralizeQualificationAnswerInput } from '@/lib/viralize-qualification/questionnaire';
import { shouldSkipViralizeIntroVideo } from '@/lib/viralize-qualification/shouldSkipIntroVideo';

import {
  ViralizeQualificationModal,
  type ViralizeQualificationContactInput,
} from './ViralizeQualificationModal';

const COURSE_CHECKOUT_PATH = '/courses/viralize-em-4-semanas/checkout';
const COURSE_PAGE_PATH = '/courses/viralize-em-4-semanas';
const VIRALIZE_COURSE_SLUG = 'viralize-em-4-semanas';

type QualificationPhase = 'idle' | 'loading' | 'error' | 'question';
type ProviderProfile = {
  name?: string | null;
  phone_number?: string | null;
};

interface SessionState {
  profile: ProviderProfile;
  qualification: ViralizeQualificationSnapshot | null;
  itinerary: ReturnType<typeof buildViralizeQualificationItinerary>;
}

export interface ViralizeQualificationProviderProps {
  children: ReactNode;
  profile: ProviderProfile;
  profileReady?: boolean;
  isAdmin?: boolean;
  onProfileSaved?: (profile: {
    name?: string | null;
    phone_number?: string | null;
  }) => void | Promise<unknown>;
  onActiveChange?: (active: boolean) => void;
}

function connectedEntryFallback(): HTMLElement | null {
  const candidate = document.querySelector<HTMLElement>(
    '[data-viralize-course-entry]',
  );
  return candidate?.isConnected ? candidate : null;
}

function LoadingOrErrorDialog({
  phase,
  onClose,
  onRetry,
}: {
  phase: 'loading' | 'error';
  onClose: () => void;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  const title = t(
    phase === 'loading'
      ? 'viralizeQualification.loading'
      : 'viralizeQualification.loadError',
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        data-sentry-block=""
        data-viralize-qualification-private=""
        className="rr-block w-[calc(100%-2rem)] max-w-md border-primary/25 bg-card"
        hideCloseButton
      >
        <DialogClose asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-2 top-2 size-11 rounded-full"
            aria-label={t('viralizeQualification.close')}
          >
            <X aria-hidden="true" />
          </Button>
        </DialogClose>
        <DialogTitle className="pr-12 text-xl">{title}</DialogTitle>
        <DialogDescription>
          {phase === 'loading'
            ? t('viralizeQualification.loadingDescription')
            : t('viralizeQualification.loadError')}
        </DialogDescription>
        {phase === 'error' && (
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('viralizeQualification.close')}
            </Button>
            <Button type="button" onClick={onRetry}>
              {t('viralizeQualification.retry')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ViralizeQualificationProvider({
  children,
  profile,
  profileReady = true,
  isAdmin = false,
  onProfileSaved,
  onActiveChange,
}: ViralizeQualificationProviderProps) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<QualificationPhase>('idle');
  const [session, setSession] = useState<SessionState | null>(null);
  const { refetch } = useMyViralizeQualificationQuery({
    skip: true,
    fetchPolicy: 'network-only',
  });
  const [loadCourse] = useGetCourseBySlugLazyQuery();
  const [saveContactMutation] =
    useSaveMyViralizeQualificationContactMutation();
  const [saveAnswerMutation] = useSaveMyViralizeQualificationAnswerMutation();
  const mountedRef = useRef(false);
  const generationRef = useRef(0);
  const triggerRef = useRef<HTMLElement | null>(null);
  const replayQuestionnaireRef = useRef(false);
  const profileRef = useRef(profile);
  const profileReadyRef = useRef(profileReady);
  const isAdminRef = useRef(isAdmin);
  const onProfileSavedRef = useRef(onProfileSaved);
  const onActiveChangeRef = useRef(onActiveChange);
  const activeNotifiedRef = useRef(false);
  const completionHandledRef = useRef(false);

  profileRef.current = profile;
  profileReadyRef.current = profileReady;
  isAdminRef.current = isAdmin;
  onProfileSavedRef.current = onProfileSaved;
  onActiveChangeRef.current = onActiveChange;

  const notifyActive = useCallback((active: boolean) => {
    if (activeNotifiedRef.current === active) return;
    activeNotifiedRef.current = active;
    onActiveChangeRef.current?.(active);
  }, []);

  const restoreFocus = useCallback(() => {
    const requestedTrigger = triggerRef.current;
    triggerRef.current = null;
    window.setTimeout(() => {
      const target =
        requestedTrigger?.isConnected === true
          ? requestedTrigger
          : connectedEntryFallback();
      target?.focus({ preventScroll: true });
    }, 0);
  }, []);

  const close = useCallback(
    (restore = true) => {
      generationRef.current += 1;
      setPhase('idle');
      setSession(null);
      notifyActive(false);
      if (restore) restoreFocus();
      else triggerRef.current = null;
    },
    [notifyActive, restoreFocus],
  );

  const startRequest = useCallback(
    async (
      trigger?: HTMLElement | null,
      options?: RequestViralizeCourseOptions,
    ): Promise<void> => {
      if (!profileReadyRef.current) return;

      const replayQuestionnaire = options?.replayQuestionnaire === true;
      if (
        shouldSkipViralizeIntroVideo({
          hasCourseAccess: isAdminRef.current,
          replayQuestionnaire,
        })
      ) {
        navigate(COURSE_PAGE_PATH);
        return;
      }

      const generation = ++generationRef.current;
      completionHandledRef.current = false;
      replayQuestionnaireRef.current = replayQuestionnaire;
      triggerRef.current = trigger instanceof HTMLElement ? trigger : null;
      setSession(null);
      setPhase('loading');
      notifyActive(true);

      try {
        const [courseResult, result] = await Promise.all([
          replayQuestionnaire
            ? Promise.resolve({ data: { course: null } })
            : loadCourse({
                variables: { slug: VIRALIZE_COURSE_SLUG },
                fetchPolicy: 'network-only',
              }).catch(() => ({ data: { course: null } })),
          refetch(),
        ]);
        if (!mountedRef.current || generationRef.current !== generation) return;

        if (
          shouldSkipViralizeIntroVideo({
            hasCourseAccess: hasCourseAccess({
              ownedByViewer: !!courseResult.data?.course?.ownedByViewer,
              isAdmin: isAdminRef.current,
            }),
            replayQuestionnaire,
          })
        ) {
          setPhase('idle');
          setSession(null);
          notifyActive(false);
          triggerRef.current = null;
          navigate(COURSE_PAGE_PATH);
          return;
        }

        const qualification = result.data?.myViralizeQualification ?? null;
        const sessionProfile = { ...profileRef.current };
        const sessionQualification = replayQuestionnaire ? null : qualification;
        // Skip the questionnaire and open the post-quiz intro video.
        const itinerary = Object.freeze(['intro_video' as const]);

        setSession({
          profile: sessionProfile,
          qualification: sessionQualification,
          itinerary,
        });
        setPhase('question');
      } catch {
        if (!mountedRef.current || generationRef.current !== generation) return;
        setSession(null);
        setPhase('error');
      }
    },
    [loadCourse, navigate, notifyActive, refetch],
  );

  const retry = useCallback(() => {
    void startRequest(triggerRef.current, {
      replayQuestionnaire: replayQuestionnaireRef.current,
    });
  }, [startRequest]);

  const saveContact = useCallback(
    async (input: ViralizeQualificationContactInput): Promise<void> => {
      const variables: ViralizeQualificationContactInput = {};
      if (input.name !== undefined) variables.name = input.name;
      if (input.phoneDetails !== undefined) {
        variables.phoneDetails = input.phoneDetails;
      }
      const result = await saveContactMutation({ variables });
      const saved = result.data?.saveMyViralizeQualificationContact;
      if (!saved) throw new Error('Viralize qualification contact was not saved.');

      const savedProfile = {
        name: saved.name,
        phone_number: saved.phone_number,
      };
      setSession((current) =>
        current
          ? { ...current, profile: { ...current.profile, ...savedProfile } }
          : current,
      );

      try {
        await onProfileSavedRef.current?.(savedProfile);
      } catch (error) {
        console.warn('Unable to refresh profile after qualification contact save', error);
      }
    },
    [saveContactMutation],
  );

  const saveAnswer = useCallback(
    async (
      input: ViralizeQualificationAnswerInput,
    ): Promise<ViralizeQualificationSnapshot> => {
      const result = await saveAnswerMutation({ variables: { input } });
      const saved = result.data?.saveMyViralizeQualificationAnswer;
      if (!saved) throw new Error('Viralize qualification answer was not saved.');
      return saved;
    },
    [saveAnswerMutation],
  );

  const complete = useCallback(() => {
    if (phase !== 'question' || completionHandledRef.current) return;
    completionHandledRef.current = true;
    generationRef.current += 1;
    setPhase('idle');
    setSession(null);
    notifyActive(false);
    triggerRef.current = null;
    navigate(COURSE_CHECKOUT_PATH);
  }, [navigate, notifyActive, phase]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      generationRef.current += 1;
      if (activeNotifiedRef.current) {
        activeNotifiedRef.current = false;
        onActiveChangeRef.current?.(false);
      }
    };
  }, []);

  const contextValue = useMemo(
    () => ({ requestViralizeCourse: startRequest }),
    [startRequest],
  );

  return (
    <ViralizeQualificationContext.Provider value={contextValue}>
      {children}
      {(phase === 'loading' || phase === 'error') && (
        <LoadingOrErrorDialog
          phase={phase}
          onClose={() => close(true)}
          onRetry={retry}
        />
      )}
      {phase === 'question' && session && (
        <ViralizeQualificationModal
          open
          itinerary={session.itinerary}
          profile={session.profile}
          qualification={session.qualification}
          onSaveContact={saveContact}
          onSaveAnswer={saveAnswer}
          onComplete={complete}
          onClose={() => close(true)}
        />
      )}
    </ViralizeQualificationContext.Provider>
  );
}
